import { yupResolver } from '@hookform/resolvers/yup';
import SyncIcon from '@mui/icons-material/Sync';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Stack from '@mui/system/Stack';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DesktopDateTimePicker } from '@mui/x-date-pickers/DesktopDateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
	DATE_PRECISION_PLACEHOLDER,
	DATE_PRECISION_VIEWS,
} from '../../../../../fastForm/data/constants';
import {
	TABLES_CLEAR_BUTTON_LABEL,
	TABLES_REFRESH_BUTTON_LABEL,
	TABLES_SEARCH_BUTTON_LABEL,
	TABLES_SEARCH_INPUT_LABEL,
	TABLES_START_DATE_INPUT_LABEL,
} from '../../../data/constants';

interface IFormValues {
	tableName: string;
	asOfDate: dayjs.Dayjs;
}

interface IFormInputs {
	tableName: string;
	asOfDate: number;
}

interface FilterScriptLogsProps extends IFormInputs {
	onApply: (data: IFormInputs) => void;
	refetch: () => void;
}

const FilterScriptLogs = (props: FilterScriptLogsProps) => {
	const { onApply, asOfDate, tableName, refetch } = props;

	const navigate = useNavigate();
	const location = useLocation();
	const { handleSubmit, control, reset } = useForm<IFormValues>({
		defaultValues: {
			asOfDate: asOfDate ? dayjs(asOfDate) : null,
			tableName,
		},
		resolver: yupResolver(
			yup.object().shape({
				tableName: yup.string().nullable(),
				asOfDate: yup.date().nullable(),
			}),
		),
	});

	const onSubmit: SubmitHandler<IFormValues> = (data) => {
		const { tableName, asOfDate } = data;
		const request = {} as IFormInputs;

		if (tableName) {
			request.tableName = tableName;
		}
		if (asOfDate) {
			request.asOfDate = asOfDate.valueOf();
		}

		onApply(request);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Stack
				direction='row'
				justifyContent='space-between'
				alignItems='flex-end'
			>
				<Stack
					alignItems={{
						xs: 'flex-start',
						sm: 'flex-start',
						md: 'flex-start',
						lg: 'center',
					}}
					component='form'
					direction={{ xs: 'column', sm: 'column', md: 'column', lg: 'row' }}
					spacing={1}
					onSubmit={handleSubmit(onSubmit)}
				>
					<Controller
						name='tableName'
						control={control}
						render={({ field }) => (
							<TextField
								label={TABLES_SEARCH_INPUT_LABEL()}
								variant='outlined'
								size='small'
								sx={{ backgroundColor: 'white.lightest', width: '233px' }}
								{...field}
							/>
						)}
					/>
					<Controller
						name='asOfDate'
						control={control}
						render={({ field }) => (
							<DesktopDateTimePicker
								inputFormat={DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.HOUR]}
								label={TABLES_START_DATE_INPUT_LABEL()}
								views={DATE_PRECISION_VIEWS[DATE_PRECISION.HOUR]}
								disableMaskedInput={false}
								{...field}
								renderInput={(params) => (
									<TextField
										variant='outlined'
										size='small'
										sx={{ backgroundColor: 'white.lightest', width: '233px' }}
										{...params}
										inputProps={{
											...params.inputProps,
											placeholder:
												DATE_PRECISION_PLACEHOLDER[DATE_PRECISION.HOUR],
										}}
									/>
								)}
							/>
						)}
					/>
					<Button variant='contained' color='secondary' type='submit'>
						{TABLES_SEARCH_BUTTON_LABEL()}
					</Button>
					<Button
						variant='text'
						color='neutralButton'
						onClick={() => {
							reset({
								tableName: '',
								asOfDate: null,
							});
							navigate({
								pathname: location.pathname,
								search: '',
							});
						}}
					>
						{TABLES_CLEAR_BUTTON_LABEL()}
					</Button>
				</Stack>{' '}
				<Button
					variant='outlined'
					color='neutralButton'
					onClick={() => refetch()}
					startIcon={<SyncIcon />}
				>
					{TABLES_REFRESH_BUTTON_LABEL()}
				</Button>
			</Stack>
		</LocalizationProvider>
	);
};

export default FilterScriptLogs;
